import * as React from "react";
import { authService, type LoginCredentials } from "@/services/authService";
import { authEvents } from "@/services/authEvents";
import { tokenStorage } from "@/services/tokenStorage";
import { extractApiErrorMessage } from "@/utils/apiErrors";

/**
 * Owns AUTHENTICATED APPLICATION STATE only (`status`) and orchestrates
 * when tokens get written/cleared - it does not perform HTTP calls itself
 * (`authService`'s job) and does not touch `localStorage` directly
 * (`tokenStorage`'s job). Route protection (`ProtectedRoute`) and UI
 * (`LoginPage`, `TopNavigation`'s logout button) consume this context but
 * contain no auth logic of their own.
 *
 * The frontend never decodes the JWT it receives - `status` is derived
 * purely from "do we currently hold tokens that the backend has accepted
 * or that we're about to try," never from inspecting token claims. Any
 * authorization decision (what a user may or may not do) remains entirely
 * the backend's, enforced per-request - this context only tracks whether
 * the browser currently has a plausible session, for routing/UX purposes.
 */

export type AuthStatus = "bootstrapping" | "authenticated" | "unauthenticated";

interface AuthContextValue {
  status: AuthStatus;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [status, setStatus] = React.useState<AuthStatus>("bootstrapping");
  // Read inside the bootstrap effect's async callbacks so a manual
  // login()/logout() that resolves WHILE the background silent-refresh
  // check is still in flight always wins - without this, a slower
  // bootstrap() that later fails would call setStatus("unauthenticated")
  // and clear the tokens a just-completed, successful login() had already
  // stored, silently ejecting the user right after signing in. Once
  // status has moved away from "bootstrapping" for any reason, the
  // bootstrap check's own result is stale and must be discarded.
  const statusRef = React.useRef(status);
  statusRef.current = status;

  React.useEffect(() => {
    // Silent refresh on app load: a stored refresh token means the user
    // was previously logged in - exchange it for a fresh access token
    // once, so a page reload doesn't force re-login mid-session. This
    // runs exactly once on mount (no retry loop) - if it fails, the
    // refresh token itself is invalid/expired and the user must log in
    // again, which is the correct outcome, not an error to retry.
    let cancelled = false;

    async function bootstrap(): Promise<void> {
      const refreshToken = tokenStorage.getRefreshToken();
      if (!refreshToken) {
        if (!cancelled) setStatus("unauthenticated");
        return;
      }
      try {
        const tokens = await authService.refresh(refreshToken);
        if (cancelled || statusRef.current !== "bootstrapping") return;
        tokenStorage.setTokens(tokens);
        setStatus("authenticated");
      } catch {
        if (cancelled || statusRef.current !== "bootstrapping") return;
        tokenStorage.clearTokens();
        setStatus("unauthenticated");
      }
    }

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    // apiClient's own response interceptor emits this when a background
    // token refresh (triggered by a 401 on some other request) fails -
    // the session is over; reflect that here so ProtectedRoute redirects.
    return authEvents.onSessionExpired(() => {
      setStatus("unauthenticated");
    });
  }, []);

  const login = React.useCallback(async (credentials: LoginCredentials): Promise<void> => {
    try {
      const tokens = await authService.login(credentials);
      tokenStorage.setTokens(tokens);
      setStatus("authenticated");
    } catch (error) {
      // Never store partial/failed credentials.
      tokenStorage.clearTokens();
      throw new Error(
        extractApiErrorMessage(error, "Unable to sign in. Please check your credentials and try again."),
      );
    }
  }, []);

  const logout = React.useCallback(async (): Promise<void> => {
    try {
      await authService.logout();
    } catch {
      // A failed logout call (network error, already-expired token, ...)
      // must never leave the user stuck "logged in" client-side - local
      // credentials are cleared regardless of the backend call's outcome.
    } finally {
      tokenStorage.clearTokens();
      setStatus("unauthenticated");
    }
  }, []);

  const value = React.useMemo(() => ({ status, login, logout }), [status, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
